import { useLabUsers, useTasksFromOrderId } from '@witmetrics/data-store';
import {
  filterUnisonTasks,
  getTaskFilterCount,
} from '@witmetrics/utils/filters';
import { isEmpty } from '@witmetrics/utils/misc';
import { mapToKey } from '@witmetrics/utils/arrays';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useIds } from '@/hooks/useIds';
import Card from '@/components/Card';
import OrderTasksList from '@/components/OrderTasksList';
import OrderTaskDialog from '@/dialogs/OrderTaskDialog';
import TasksFilterMenu from '@/menus/TasksFilterMenu';
import SectionTitle from '../../SectionTitle';
import Section from '../../Section';
import EmptyMessage from '../../EmptyMessage';
import StatusToggle from './StatusToggle';
import { classes } from './utils';

export type TasksSectionProps = {
  isFetchingData: boolean;
};

export default function TasksSection({ isFetchingData }: TasksSectionProps) {
  const { activeOrderId } = useAppState();
  const labUsers = useLabUsers();
  const tasks = useTasksFromOrderId(activeOrderId);
  const [userIds, setUserIds] = useIds(mapToKey(labUsers, 'userId'));
  const [showAll, toggleShowAll] = useToggle();
  const [isTaskDialogShown, toggleTaskDialog] = useToggle();

  const getFiltersCount = () => {
    return getTaskFilterCount([], userIds, labUsers);
  };

  const renderTasks = () => {
    if (isEmpty(tasks)) {
      return <EmptyMessage>No tasks for current order.</EmptyMessage>;
    }
    let filteredTasks = filterUnisonTasks(tasks, [], userIds);
    if (!showAll) {
      // Only show unresolved tasks
      filteredTasks = filteredTasks.filter(({ task }) => !task.isResolved);
    }
    if (isEmpty(filteredTasks)) {
      return <EmptyMessage>No tasks matching current filters.</EmptyMessage>;
    }
    return (
      <Card className={classes.card}>
        <OrderTasksList
          isFetchingData={isFetchingData}
          className={classes.list}
          orderTasks={filteredTasks}
        />
      </Card>
    );
  };

  if (isFetchingData || !activeOrderId) return null;

  return (
    <Section>
      {isTaskDialogShown && (
        <OrderTaskDialog
          orderId={activeOrderId!}
          onClose={() => toggleTaskDialog(false)}
        />
      )}
      <SectionTitle
        title="Tasks"
        info="Tasks are only visible to lab members & not to customers."
        newLabel="New task"
        filtersCount={getFiltersCount()}
        onAddNew={() => toggleTaskDialog(true)}
        renderControls={() => (
          <StatusToggle
            className={classes.toggle}
            optionClassName={classes.toggleOption}
            value={showAll}
            onChange={toggleShowAll}
          />
        )}
        renderFilterMenu={(props) => (
          <TasksFilterMenu
            userFilters={userIds}
            onApplyFilters={(update) => setUserIds(update.userFilters)}
            {...props}
          />
        )}
      />
      {renderTasks()}
    </Section>
  );
}
